import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  IconButton
} from '@mui/material'
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Download as DownloadIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import apiClient from '../../services/api'
import toast from 'react-hot-toast'
import ReportEditor from '../ReportEditor'

interface ReportViewerDialogProps {
  open: boolean
  reportId: string | null
  reportStatus: 'draft' | 'final'
  onClose: () => void
}

interface ReportData {
  id: string
  study_id: string
  status: 'draft' | 'final'
  html?: string
  editor_html?: string
  editor_json?: any
  version: number
  patient_name?: string
  patient_cnp?: string
  patient_dob?: string
  patient_sex?: string
  study_description?: string
  study_date?: string
  study_modality?: string
}

const ReportViewerDialog: React.FC<ReportViewerDialogProps> = ({
  open,
  reportId,
  reportStatus,
  onClose
}) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [reportData, setReportData] = useState<ReportData | null>(null)

  useEffect(() => {
    if (open && reportId) {
      if (reportStatus === 'final') {
        loadPDFUrl(reportId)
      } else if (reportStatus === 'draft') {
        loadReportForViewing(reportId)
      }
    }
    
    // Cleanup on close
    return () => {
      setPdfUrl(null)
      setReportData(null)
    }
  }, [open, reportId, reportStatus])

  const loadPDFUrl = async (id: string) => {
    setLoading(true)
    try {
      const response = await apiClient.get(`/reports/${id}/pdf-url`)
      if (response.status === 200 && response.data.pdf_url) {
        setPdfUrl(response.data.pdf_url)
      } else {
        throw new Error('Nu s-a putut obține URL-ul PDF')
      }
    } catch (err: any) {
      console.error('Error loading PDF URL:', err)
      const errorMsg = err.response?.data?.detail || 'Eroare la încărcarea PDF-ului'
      toast.error(errorMsg)
      onClose()
    } finally {
      setLoading(false)
    }
  }

  const loadReportForViewing = async (id: string) => {
    setLoading(true)
    try {
      const response = await apiClient.get(`/reports/${id}`)
      if (response.status === 200) {
        setReportData(response.data)
      } else {
        throw new Error('Nu s-a putut încărca raportul')
      }
    } catch (err: any) {
      console.error('Error loading report:', err)
      const errorMsg = err.response?.data?.detail || 'Eroare la încărcarea raportului'
      toast.error(errorMsg)
      onClose()
    } finally {
      setLoading(false)
    }
  }

  const handleEditReport = () => {
    if (reportId) {
      navigate(`/reports?reportId=${reportId}`)
      onClose()
    }
  }

  const handleDownloadPDF = async () => {
    if (!reportId) return
    
    try {
      toast.loading('Se descarcă PDF-ul...', { id: 'download-pdf' })
      const response = await apiClient.get(`/reports/${reportId}/pdf`, {
        responseType: 'blob'
      })
      
      if (response.status === 200) {
        const blob = response.data
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `raport-${reportId}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        toast.success('PDF descărcat cu succes', { id: 'download-pdf' })
      } else {
        throw new Error('Eroare la descărcarea PDF-ului')
      }
    } catch (error: any) {
      console.error('Download error:', error)
      const errorMsg = error.response?.data?.detail || 'Eroare la descărcarea PDF-ului'
      toast.error(errorMsg, { id: 'download-pdf' })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { 
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        py: 2, 
        px: 3,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6">
            Raport {reportStatus === 'draft' ? '(Draft)' : '(Finalizat)'}
          </Typography>
          <IconButton
            edge="end"
            onClick={onClose}
            aria-label="close"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Se încarcă raportul...
            </Typography>
          </Box>
        ) : reportStatus === 'final' && pdfUrl ? (
          // PDF viewer pentru rapoarte finalizate
          <object
            data={pdfUrl}
            type="application/pdf"
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title="PDF Viewer"
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, flexDirection: 'column', p: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                PDF-ul nu poate fi afișat în browser.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => window.open(pdfUrl, '_blank')}
                sx={{ mt: 2 }}
              >
                Deschide în fereastră nouă
              </Button>
            </Box>
          </object>
        ) : reportStatus === 'draft' && reportData ? (
          // ReportEditor în readOnly mode pentru draft-uri
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <ReportEditor
              reportId={reportData.id}
              studyId={reportData.study_id}
              initialContent={reportData.editor_html || reportData.html || ''}
              initialJson={reportData.editor_json}
              status="draft"
              readOnly={true}
              patientInfo={{
                name: reportData.patient_name || 'N/A',
                cnp: reportData.patient_cnp || 'N/A',
                dob: reportData.patient_dob || 'N/A',
                sex: reportData.patient_sex || 'N/A'
              }}
              studyInfo={{
                description: reportData.study_description || 'N/A',
                date: reportData.study_date || 'N/A',
                modality: reportData.study_modality || 'N/A'
              }}
              onEdit={handleEditReport}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography variant="body2" color="text.secondary">
              Nu s-au putut încărca datele raportului
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: 1, 
        borderColor: 'divider', 
        px: 3, 
        py: 2
      }}>
        {reportStatus === 'draft' && reportData && (
          <Button
            startIcon={<EditIcon />}
            variant="contained"
            onClick={handleEditReport}
          >
            Editează
          </Button>
        )}
        {reportStatus === 'final' && (
          <Button
            startIcon={<DownloadIcon />}
            variant="contained"
            onClick={handleDownloadPDF}
          >
            Descarcă PDF
          </Button>
        )}
        <Button 
          onClick={onClose} 
          variant="outlined"
        >
          Închide
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ReportViewerDialog

